import { useCallback, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { CorporateUserType } from 'corporate/enums';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { RemoveCustomersButton } from 'jobs/components/JobDetails/tabs/CustomerList/actions';
import { useJobCustomerActions } from 'jobs/hooks';
import { IJobCustomer } from 'jobs/types';

import { schema } from './schema';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: number;
  projectId: number;
  customer?: IJobCustomer;
}

interface ICustomerFormData {
  corporateUser: ICorporateUserAutocomplete;
  externalCustomerNumber: string;
}

export const UpsertCustomer = ({ isOpen, onClose, jobId, projectId, customer }: IProps) => {
  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { updateRows } = useAgGridClientTableContext();
  const { upsertJobCustomer } = useJobCustomerActions();

  const isEdit = Boolean(customer?.id);

  const defaultValues = useMemo(() => {
    if (!isOpen || !isEdit) {
      return {};
    }

    return {
      corporateUser: {
        id: customer.foreignCorporateUserId,
        companyId: customer.foreignCompanyId,
      },
      externalCustomerNumber: customer.externalCustomerNumber || '',
    };
  }, [isOpen, isEdit, customer]);

  const removeButton = useMemo(() => {
    return isEdit ? <RemoveCustomersButton jobId={jobId} customers={[customer]} /> : null;
  }, [customer, jobId, isEdit]);

  const handleOnClose = useCallback(
    (reset: UseFormReset<ICustomerFormData>) => {
      reset(defaultValues);
      clearNotification();
      onClose();
    },
    [defaultValues, onClose, clearNotification]
  );

  const onSubmitSuccess = useCallback(
    (reset: UseFormReset<ICustomerFormData>, updatedContact: IJobCustomer) => {
      const updateRowsData = isEdit ? { update: [updatedContact] } : { add: [updatedContact] };

      updateRows(updateRowsData);

      setTimeout(() => {
        handleOnClose(reset);
      }, 1000);
    },
    [isEdit, updateRows, handleOnClose]
  );

  const onSubmit = useCallback(
    (reset: UseFormReset<ICustomerFormData>) => async (data: ICustomerFormData) => {
      if (!data.corporateUser) {
        return;
      }

      const { success, bomCustomerCompanyAssociation: updatedContact } = await upsertJobCustomer({
        bomCustomerCompanyAssociation: {
          bomId: jobId,
          foreignCorporateUserId: data.corporateUser.id,
          foreignCompanyId: data.corporateUser.companyId,
          externalCustomerNumber: data.externalCustomerNumber || null,
          ...(isEdit && { id: customer?.id }),
        },
      });

      if (!success) {
        return;
      }

      onSubmitSuccess(reset, updatedContact);
    },
    [jobId, isEdit, customer, upsertJobCustomer, onSubmitSuccess]
  );

  return (
    <FormProvider schema={schema()} defaultValues={defaultValues} resetDefaultValues>
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        title={isEdit ? t('titleEditCustomer') : t('titleAddCustomer')}
        subtitle={customer?.foreignContactGroupName}
        onSubmit={onSubmit}
        submitBtnLabel={isEdit ? t('buttonLabelUpdateCustomer') : t('buttonLabelAddCustomer')}
        extraButtons={removeButton}
      >
        {!isEdit && (
          <FormField
            name="corporateUser"
            type={InputType.AUTOCOMPLETE}
            inputProps={{
              label: t('labelQuoteContact'),
              placeholder: t('placeholderSelectCustomer'),
              getOptions: corporateUserService.getCorporateForeignUsersAutocomplete,
              requestPayload: {
                filters: {
                  userType: CorporateUserType.CUSTOMER,
                  projectId,
                },
              },
              getKey: (option: ICorporateUserAutocomplete) =>
                `${option.id}-${option.contactGroup?.id}`,
              getLabel: (option: ICorporateUserAutocomplete) =>
                option.contactGroup?.name
                  ? `${option.name} (${option.contactGroup.name})`
                  : option.name,
              disableClearable: true,
              required: true,
            }}
          />
        )}

        <FormField
          name="externalCustomerNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelCustomerNumber'),
            placeholder: t('placeholderEnterDigits', { count: 6 }),
            slotProps: {
              input: {
                maxLength: 6,
                pattern: '[0-9]{6}',
              },
            },
          }}
        />
      </Form>
    </FormProvider>
  );
};
